import { supabase } from "../supabase";

export type StarterTourFeature = "meal_builder" | "pantry" | "health_tracking";

export type StarterTourProgress = {
  mealBuilderVisited: boolean;
  pantryVisited: boolean;
  healthTrackingVisited: boolean;
};

export type AdminUserOverview = {
  totalAccounts: number;
  confirmedAccounts: number;
  active7Days: number;
  active30Days: number;
  new30Days: number;
  completeAccessAccounts: number;
  paidAccounts: number;
  nonPaidAccounts: number;
  complimentaryAccounts: number;
  ownerAccounts: number;
  trialAccounts: number;
  paymentAttentionAccounts: number;
  earlyAccessAccounts: number;
  trackingSince: string;
  updatedAt: string;
};

export type AdminUserDetail = {
  userId: string;
  signInName: string;
  fullName: string | null;
  firstName: string | null;
  lastName: string | null;
  createdAt: string;
  confirmedAt: string | null;
  lastSeenAt: string | null;
  planCode: "free" | "household";
  accessSource: "free" | "paid" | "owner" | "support" | "promo_code" | "administrator";
  ownerGrantActive: boolean;
  canManageComplimentaryAccess: boolean;
  accessActionBlockedReason: string | null;
  canDelete: boolean;
  deleteBlockedReason: string | null;
};

export type AdminBillingDetail = {
  userId: string;
  billingStatus: "trialing" | "active" | "past_due" | "canceled" | "unpaid" | "incomplete" | "paused";
  hasPaidAccess: boolean;
  cancellationScheduled: boolean;
  scheduledEndAt: string | null;
  renewsAt: string | null;
  currentPeriodEnd: string | null;
  paymentAttentionReason: string | null;
  paymentAttentionSince: string | null;
  billingUpdatedAt: string | null;
};

export async function recordPrecisePlateActivity(): Promise<void> {
  const { error } = await supabase.rpc("record_precise_plate_activity");
  if (error) throw error;
}

export function starterTourFeatureForPath(pathname: string): StarterTourFeature | null {
  if (pathname === "/meal-builder") return "meal_builder";
  if (pathname === "/pantry") return "pantry";
  if (["/health-tracking", "/hydration", "/weight-log", "/health-measurements", "/glucose-trends"].includes(pathname)) return "health_tracking";
  return null;
}

export async function recordStarterTourVisit(feature: StarterTourFeature, userId: string): Promise<void> {
  window.localStorage.setItem(starterTourVisitStorageKey(userId, feature), "1");
  const { error } = await supabase.rpc("record_precise_plate_tour_visit", { feature_key: feature });
  if (error) throw error;
}

export async function getStarterTourProgress(userId: string): Promise<StarterTourProgress> {
  const { data, error } = await supabase.rpc("get_my_precise_plate_activity");
  if (error) throw error;
  const value = typeof data === "object" && data !== null ? data as Record<string, unknown> : {};
  return {
    mealBuilderVisited: Boolean(value.meal_builder_visited_at) || hasLocalStarterTourVisit(userId, "meal_builder"),
    pantryVisited: Boolean(value.pantry_visited_at) || hasLocalStarterTourVisit(userId, "pantry"),
    healthTrackingVisited: Boolean(value.health_tracking_visited_at) || hasLocalStarterTourVisit(userId, "health_tracking"),
  };
}

function starterTourVisitStorageKey(userId: string, feature: StarterTourFeature): string {
  return `precise-plate-tour-visit:${userId}:${feature}`;
}

function hasLocalStarterTourVisit(userId: string, feature: StarterTourFeature): boolean {
  return window.localStorage.getItem(starterTourVisitStorageKey(userId, feature)) === "1";
}

export async function getAdminUserOverview(): Promise<AdminUserOverview> {
  const { data, error } = await supabase.rpc("get_precise_plate_user_overview");
  if (error) throw error;
  const value = typeof data === "object" && data !== null ? data as Record<string, unknown> : {};
  return {
    totalAccounts: numberValue(value.totalAccounts),
    confirmedAccounts: numberValue(value.confirmedAccounts),
    active7Days: numberValue(value.active7Days),
    active30Days: numberValue(value.active30Days),
    new30Days: numberValue(value.new30Days),
    completeAccessAccounts: numberValue(value.completeAccessAccounts),
    paidAccounts: numberValue(value.paidAccounts),
    nonPaidAccounts: numberValue(value.nonPaidAccounts),
    complimentaryAccounts: numberValue(value.complimentaryAccounts),
    ownerAccounts: numberValue(value.ownerAccounts),
    trialAccounts: numberValue(value.trialAccounts),
    paymentAttentionAccounts: numberValue(value.paymentAttentionAccounts),
    earlyAccessAccounts: numberValue(value.earlyAccessAccounts),
    trackingSince: String(value.trackingSince ?? ""),
    updatedAt: String(value.updatedAt ?? ""),
  };
}

export async function getAdminUserDetails(): Promise<AdminUserDetail[]> {
  const { data, error } = await supabase.rpc("get_precise_plate_user_details");
  if (error) throw error;
  return Array.isArray(data) ? data.map((value) => userDetailValue(value)) : [];
}

export async function adminDeletePrecisePlateProfile(userId: string, confirmation: string): Promise<void> {
  const { data, error } = await supabase.rpc("admin_delete_precise_plate_profile", {
    target_user_id: userId,
    confirmation,
  });
  if (error) throw error;
  if (!data || typeof data !== "object" || (data as { status?: unknown }).status !== "deleted") {
    throw new Error("Precise Plate could not confirm that profile deletion completed.");
  }
}

export async function getAdminBillingDetails(): Promise<AdminBillingDetail[]> {
  const { data, error } = await supabase.rpc("get_precise_plate_admin_billing_details");
  if (error) throw error;
  return Array.isArray(data) ? data.map((value) => adminBillingDetailValue(value)) : [];
}

export async function setAdminComplimentaryHouseholdAccess(userId: string, enabled: boolean): Promise<void> {
  const { data, error } = await supabase.rpc("set_precise_plate_user_complimentary_access", {
    target_user_id: userId,
    p_enabled: enabled,
  });
  if (error) throw error;
  if (!data || typeof data !== "object" || !["updated", "unchanged"].includes(String((data as { status?: unknown }).status ?? ""))) {
    throw new Error("Precise Plate could not confirm the access change.");
  }
}

function userDetailValue(value: unknown): AdminUserDetail {
  const row = typeof value === "object" && value !== null ? value as Record<string, unknown> : {};
  return {
    userId: stringValue(row.userId),
    signInName: stringValue(row.signInName),
    fullName: nullableString(row.fullName),
    firstName: nullableString(row.firstName),
    lastName: nullableString(row.lastName),
    createdAt: stringValue(row.createdAt),
    confirmedAt: nullableString(row.confirmedAt),
    lastSeenAt: nullableString(row.lastSeenAt),
    planCode: row.planCode === "household" ? "household" : "free",
    accessSource: accessSourceValue(row.accessSource),
    ownerGrantActive: row.ownerGrantActive === true,
    canManageComplimentaryAccess: row.canManageComplimentaryAccess === true,
    accessActionBlockedReason: nullableString(row.accessActionBlockedReason),
    canDelete: row.canDelete === true,
    deleteBlockedReason: nullableString(row.deleteBlockedReason),
  };
}

function adminBillingDetailValue(value: unknown): AdminBillingDetail {
  const row = typeof value === "object" && value !== null ? value as Record<string, unknown> : {};
  return {
    userId: stringValue(row.userId),
    billingStatus: billingStatusValue(row.billingStatus),
    hasPaidAccess: row.hasPaidAccess === true,
    cancellationScheduled: row.cancellationScheduled === true,
    scheduledEndAt: nullableString(row.scheduledEndAt),
    renewsAt: nullableString(row.renewsAt),
    currentPeriodEnd: nullableString(row.currentPeriodEnd),
    paymentAttentionReason: nullableString(row.paymentAttentionReason),
    paymentAttentionSince: nullableString(row.paymentAttentionSince),
    billingUpdatedAt: nullableString(row.billingUpdatedAt),
  };
}

function accessSourceValue(value: unknown): AdminUserDetail["accessSource"] {
  return value === "paid" || value === "owner" || value === "support" || value === "promo_code" || value === "administrator"
    ? value
    : "free";
}

function billingStatusValue(value: unknown): AdminBillingDetail["billingStatus"] {
  return value === "trialing" || value === "active" || value === "past_due" || value === "canceled" || value === "unpaid" || value === "paused"
    ? value
    : "incomplete";
}

function stringValue(value: unknown): string {
  return typeof value === "string" ? value : String(value ?? "");
}

function nullableString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length ? trimmed : null;
}

function numberValue(value: unknown): number {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}
